import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Video as VideoIcon, Film, X, Loader2, Folders, ChevronRight, Check } from 'lucide-react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { VideoCardUnified } from '@/components/shared/VideoCardUnified';
import { videoService } from '@/services/videoService';
import { useCustomAuth } from '@/contexts/CustomAuthContext';
import { hierarchicalFoldersService } from '@/services/hierarchicalFoldersService';
import { CompactFolderItem } from '@/components/video-editor/CompactFolderItem';

interface Video {
  id: string;
  title: string;
  videoUrl?: string;
  url?: string;
  thumbnailUrl?: string;
  duration?: number;
  status: string;
  [key: string]: any;
}

interface VideoLibraryModalWithFoldersProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (video: Video) => void;
  title?: string;
}

export function VideoLibraryModalWithFolders({
  open,
  onOpenChange,
  onSelect,
  title = translateInterfaceText("Selecionar Vídeo"),
}: VideoLibraryModalWithFoldersProps) {
  useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isLoadingVideoData, setIsLoadingVideoData] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const { user } = useCustomAuth();

  // Fechar com Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Fetch root folders for sidebar
  const { data: foldersData } = useQuery({
    queryKey: ['video-library-folders'],
    queryFn: () => hierarchicalFoldersService.listRoot(),
    enabled: open,
    staleTime: Infinity,
  });

  // Fetch folder details when a folder is selected
  const { data: folderDetails, isLoading: isFolderLoading } = useQuery({
    queryKey: ['video-library-folder-details', currentFolderId],
    queryFn: () => hierarchicalFoldersService.getFolderDetails(currentFolderId!),
    enabled: !!currentFolderId && open,
    staleTime: Infinity,
  });

  // Infinite query for root videos — inclui vídeos dentro de pastas para
  // que "Todos os vídeos" mostre literalmente toda a biblioteca do usuário.
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ['videos-library', 'all-including-folders'],
    queryFn: ({ pageParam = 1 }) =>
      videoService.getVideosList({
        page: pageParam,
        limit: 30,
        search: undefined,
        status: undefined,
        includeFolderVideos: true,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasNextPage ? lastPage.pagination.page + 1 : undefined,
    enabled: open,
    initialPageParam: 1,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const allVideos = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap(page => page.videos);
  }, [data]);

  const processedVideos: Video[] = useMemo(() => {
    return allVideos
      .filter(v => (v.status as string) === 'processed' || v.status === 'active')
      .map(v => ({
        ...v,
        videoUrl: (v as any).videoDownload || (v as any).videoMP4 || (v as any).url || (v as any).videoUrl || '',
      }));
  }, [allVideos]);

  const filteredVideos = useMemo(() => {
    if (!searchQuery.trim()) return processedVideos;
    const searchLower = searchQuery.toLowerCase().trim();
    return processedVideos.filter(video =>
      video.title?.toLowerCase().includes(searchLower) ||
      video.nomeVideo?.toLowerCase().includes(searchLower) ||
      video.id?.toLowerCase().includes(searchLower)
    );
  }, [processedVideos, searchQuery]);

  const folderVideos: Video[] = useMemo(() => {
    if (!folderDetails?.videos) return [];
    return folderDetails.videos.map(v => ({
      id: v.id,
      title: v.title,
      videoUrl: '',
      thumbnailUrl: v.thumbnailUrl || null,
      duration: v.duration,
      status: v.status || 'active',
    }));
  }, [folderDetails]);

  const filteredFolderVideos = useMemo(() => {
    if (!searchQuery.trim()) return folderVideos;
    const searchLower = searchQuery.toLowerCase().trim();
    return folderVideos.filter(video =>
      video.title?.toLowerCase().includes(searchLower) ||
      video.id?.toLowerCase().includes(searchLower)
    );
  }, [folderVideos, searchQuery]);

  const displayVideos = currentFolderId ? filteredFolderVideos : filteredVideos;
  const displayIsLoading = !currentFolderId && isLoading && allVideos.length === 0;

  const navigateToRoot = () => {
    setCurrentFolderId(null);
    setSearchQuery('');
  };

  const navigateToFolder = (id: string) => {
    setCurrentFolderId(id);
    setSearchQuery('');
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (currentFolderId) return;
    const target = event.currentTarget;
    const scrollPercentage = (target.scrollTop + target.clientHeight) / target.scrollHeight;
    if (scrollPercentage > 0.8 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const handleConfirm = async () => {
    if (!selectedId || !user) return;
    setIsLoadingVideoData(true);
    try {
      const cachedVideo =
        processedVideos.find(v => v.id === selectedId) ||
        folderVideos.find(v => v.id === selectedId);

      if (cachedVideo) {
        onSelect(cachedVideo);
        onOpenChange(false);
        setSelectedId(null);
        setSearchQuery('');
        setCurrentFolderId(null);
      }
    } catch (error) {
      console.error('Error selecting video:', error);
    } finally {
      setIsLoadingVideoData(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    setSelectedId(null);
    setSearchQuery('');
    setCurrentFolderId(null);
  };

  const folders = foldersData?.items || [];
  const sectionLabel = searchQuery.trim()
    ? `${displayVideos.length} resultado(s)`
    : currentFolderId
      ? `${folderDetails?.name || 'Pasta'} (${displayVideos.length})`
      : `Seus Vídeos (${displayVideos.length})`;

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="vlm-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-[6px]"
        onClick={handleCancel}
      />

      {/* Modal — tamanho fixo, sem sistema de animação do Radix */}
      <div className="relative z-10 w-[95vw] sm:w-[80%] md:w-[65%] lg:w-[50%] max-w-[820px] h-[85vh] sm:h-[75vh] lg:h-[70vh] flex flex-col rounded-[20px] shadow-[0_12px_40px_rgba(0,0,0,0.18)] bg-white overflow-hidden">

        {/* Header */}
        <div className="px-4 md:px-7 pt-4 md:pt-6 pb-3 flex-shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-[#EEF4FF] flex items-center justify-center flex-shrink-0">
                <Film className="w-4 h-4 md:w-5 md:h-5 text-[#3B82F6]" />
              </div>
              <div>
                <h2 id="vlm-title" className="text-[16px] md:text-[18px] font-semibold text-[#111827]">
                  {title}
                </h2>
                <p className="text-[13px] md:text-[14px] text-[#6B7280] mt-1">
                  {translateInterfaceText("Escolha um vídeo da sua biblioteca para continuar.")}</p>
              </div>
            </div>
            <button
              onClick={handleCancel}
              className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#F3F4F6] hover:text-[#374151] transition-colors"
              aria-label={translateInterfaceText("Fechar")}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body: sidebar + main */}
        <div className="flex flex-1 min-h-0 overflow-hidden border-t border-[#F3F4F6]">

          {/* Sidebar */}
          <aside className="hidden md:flex w-[190px] border-r border-[#E5E7EB] flex-shrink-0 flex-col overflow-y-auto">
            <div
              onClick={navigateToRoot}
              className={`group flex items-center gap-2.5 px-3 py-3 min-h-[48px] cursor-pointer transition-all duration-150 ease-out border-l-[3px] ${
                currentFolderId === null
                  ? 'border-l-[#2D6CF6]'
                  : 'border-l-transparent hover:text-[#2D6CF6]'
              }`}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigateToRoot();
                }
              }}
            >
              <Folders
                className={`w-5 h-5 flex-shrink-0 stroke-[1.5px] ${
                  currentFolderId === null
                    ? 'text-[#2D6CF6]'
                    : 'text-[#6B7280] group-hover:text-[#2D6CF6]'
                }`}
              />
              <span
                className={`text-[14px] truncate ${
                  currentFolderId === null
                    ? 'text-[#2D6CF6] font-semibold'
                    : 'text-[#374151] font-normal group-hover:text-[#2D6CF6]'
                }`}
              >
                {translateInterfaceText("Todos os vídeos")}</span>
            </div>

            {folders.map(folder => (
              <CompactFolderItem
                key={folder.id}
                id={folder.id}
                name={folder.name}
                videoCount={folder.videosCount}
                onClick={navigateToFolder}
                isActive={currentFolderId === folder.id}
              />
            ))}
          </aside>

          {/* Main content */}
          <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
            {/* Search */}
            <div className="px-5 py-3">
              <div className="relative">
                <Search className="absolute left-[14px] top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-[#9CA3AF]" />
                <Input
                  placeholder={translateInterfaceText("Buscar vídeos...")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-[42px] h-[44px] bg-[#F9FAFB] rounded-[12px] border-[1.5px] border-[#D0D5DD] hover:border-[#3B82F6] focus:border-2 focus:border-[#3B82F6] focus-visible:ring-2 focus-visible:ring-[#3B82F6]/20 transition-all text-[14px] placeholder:text-[#9CA3AF]"
                />
              </div>
            </div>

            {/* Breadcrumb — exibe somente quando dentro de uma pasta */}
            {currentFolderId && folderDetails && (
              <div className="hidden md:block px-5 pb-2">
                <nav className="flex items-center gap-1 text-[13px] text-[#6B7280] flex-wrap">
                  <button onClick={navigateToRoot} className="hover:text-[#2D6CF6] transition-colors">
                    {translateInterfaceText("Todos os vídeos")}</button>
                  {folderDetails.breadcrumb.map(b => (
                    <React.Fragment key={b.id}>
                      <ChevronRight className="w-3.5 h-3.5 text-[#9CA3AF]" />
                      <button
                        onClick={() => navigateToFolder(b.id)}
                        className="hover:text-[#2D6CF6] transition-colors truncate max-w-[140px]"
                      >
                        {b.name}
                      </button>
                    </React.Fragment>
                  ))}
                  <ChevronRight className="w-3.5 h-3.5 text-[#9CA3AF]" />
                  <span className="text-[#111827] font-medium truncate max-w-[160px]">{folderDetails.name}</span>
                </nav>
              </div>
            )}

            {/* Section header */}
            <div className="hidden md:block px-5 mb-3">
              <h3 className="text-[15px] font-semibold text-foreground mb-2">
                {sectionLabel}
              </h3>
              <div className="h-px bg-[#E5E7EB] w-full" />
            </div>

            {/* Videos grid */}
            <div
              className="flex-1 overflow-y-auto px-5 pb-5 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
              onScroll={handleScroll}
            >
              {/* Subpastas — exibidas acima dos vídeos quando a pasta atual tem filhas */}
              {currentFolderId && !searchQuery.trim() && folderDetails?.children && folderDetails.children.length > 0 && (
                <div className="hidden md:block mb-4">
                  <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-2">{translateInterfaceText("Subpastas")}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {folderDetails.children.map(child => (
                      <button
                        key={child.id}
                        onClick={() => navigateToFolder(child.id)}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-[10px] border border-[#E5E7EB] hover:border-[#2D6CF6] hover:bg-[#EEF4FF] transition-colors text-left"
                      >
                        <Folders className="w-4 h-4 text-[#6B7280] flex-shrink-0" />
                        <span className="text-[14px] truncate flex-1 text-[#374151]">{child.name}</span>
                        <span className="text-[12px] text-[#9CA3AF] flex-shrink-0">{child.videosCount}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {displayIsLoading ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <div className="w-14 h-14 border-4 border-[#3B82F6] border-t-transparent rounded-full animate-spin mb-4" />
                  <h3 className="text-base font-semibold text-foreground mb-1">{translateInterfaceText("Carregando vídeos...")}</h3>
                  <p className="text-sm text-muted-foreground max-w-xs">{translateInterfaceText("Aguarde enquanto buscamos seus vídeos")}</p>
                </div>
              ) : currentFolderId && isFolderLoading ? (
                <div className="grid grid-cols-2 gap-3">
                  {[0, 1, 2, 3].map(i => (
                    <div key={i} className="h-[110px] rounded-xl bg-[#F3F4F6] animate-pulse" />
                  ))}
                </div>
              ) : displayVideos.length > 0 ? (
                <>
                  {/* Desktop: grid de cards */}
                  <div className="hidden md:grid grid-cols-2 gap-3">
                    {displayVideos.map(video => (
                      <VideoCardUnified
                        key={video.id}
                        id={video.id}
                        title={video.title}
                        thumbnail={video.thumbnailUrl}
                        duration={video.duration}
                        variant="compact"
                        selected={selectedId === video.id}
                        isProcessing={video.status === 'processing'}
                        onSelect={() => setSelectedId(video.id)}
                      />
                    ))}
                  </div>

                  {/* Mobile: lista compacta (mesmo padrão do modal do cloaker) */}
                  <div className="md:hidden border border-[#E5E7EB] rounded-[12px] overflow-hidden">
                    <div className="grid grid-cols-[80px_1fr] gap-3 px-3 py-2.5 bg-[#F9FAFB] border-b border-[#E5E7EB] font-medium text-[12px] text-[#6B7280]">
                      <div></div>
                      <div>{translateInterfaceText("Nome do Vídeo")}</div>
                    </div>
                    {displayVideos.map(video => (
                      <div
                        key={video.id}
                        className={cn(
                          'grid grid-cols-[80px_1fr] gap-3 items-center p-2.5 transition-all duration-150 cursor-pointer relative border-b border-[#F3F4F6] last:border-b-0',
                          selectedId === video.id
                            ? 'bg-[#EBF5FF] border-l-[3px] border-l-[#1D4FFF]'
                            : 'bg-white hover:bg-[#f6f8ff] border-l-[3px] border-l-transparent'
                        )}
                        onClick={() => setSelectedId(video.id)}
                      >
                        <div className="relative">
                          <img
                            src={video.thumbnailUrl || '/placeholder.svg'}
                            alt={video.title}
                            className="w-[72px] h-[40px] object-cover rounded-[6px] border border-[#E2E8F0] bg-[#F5F7FA]"
                          />
                          {selectedId === video.id && (
                            <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#1D4FFF] flex items-center justify-center shadow-sm">
                              <Check className="w-2.5 h-2.5 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-medium text-[13px] truncate text-foreground">{video.title}</h4>
                        </div>
                      </div>
                    ))}
                  </div>

                  {!currentFolderId && isFetchingNextPage && (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-[#3B82F6]" />
                      <span className="ml-2 text-sm text-muted-foreground">{translateInterfaceText("Carregando mais vídeos...")}</span>
                    </div>
                  )}

                  {!currentFolderId && !hasNextPage && filteredVideos.length > 30 && (
                    <div className="flex items-center justify-center py-8">
                      <p className="text-sm text-muted-foreground">{translateInterfaceText("Todos os vídeos foram carregados")}</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-4">
                    <VideoIcon className="w-7 h-7 text-muted-foreground" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-1">{translateInterfaceText("Nenhum vídeo encontrado")}</h3>
                  <p className="text-sm text-muted-foreground max-w-xs">
                    {searchQuery.trim()
                      ? translateInterfaceText("Não encontramos vídeos com \"{value0}\"", { value0: (searchQuery.trim()) })
                      : currentFolderId
                        ? translateInterfaceText("Esta pasta não possui vídeos")
                        : translateInterfaceText("Você ainda não tem vídeos processados")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-7 py-4 border-t border-[#E5E7EB] bg-white flex items-center justify-end gap-3 flex-shrink-0">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoadingVideoData}
            className="h-10 px-5 rounded-[10px] border-[#D0D5DD] bg-transparent hover:bg-[#F9FAFB] text-[#374151] hover:text-[#374151]"
          >
            {translateInterfaceText("Cancelar")}</Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedId || isLoadingVideoData}
            className="h-10 px-5 rounded-[10px] bg-[#3B82F6] hover:bg-[#2563EB] text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoadingVideoData ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {translateInterfaceText("Carregando...")}</>
            ) : (
              translateInterfaceText("Confirmar")
            )}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
